import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'material-react-toastify';
import { Box, IconButton, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { ProductPopularity } from '../../../interfaces/Product/ProductPopularity';
import { ComponentStyle } from '../../../interfaces/Settings/SettingsV2/ComponentStyle';
import { ProductSummary } from '../../../interfaces/Product/ProductSummary';
import { useThemeColor } from '../hooks/useThemeColor';
import { useIsMobile } from '../hooks/useIsMobile';
import {
  getProductsByPopularity,
  getRelatedProducts,
} from '../../../api/ProductSummary';
import { ProductCardPlaceholder } from './ProductCardPlaceholder';
import { ProductCard } from './ProductCard';
import { EditableStyleWrapper } from '../StyleEditor/EditableStyleWrapper';

const productCarouselCardWidth = 250;

type ProductsCarouselProps = {
  title?: string;
  popularity?: ProductPopularity;
  relatedProductID?: number;
  titleStyle?: ComponentStyle;
  dividerStyle?: ComponentStyle;
  titleStylePath?: string;
  dividerStylePath?: string;
};

export const ProductsCarousel = ({
  title,
  popularity,
  relatedProductID,
  titleStyle,
  dividerStyle,
  titleStylePath,
  dividerStylePath,
}: ProductsCarouselProps) => {
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<ProductSummary[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const isMobile = useIsMobile();

  const { primary, secondary } = useThemeColor();

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  useEffect(() => {
    if (popularity) {
      setLoading(true);
      getProductsByPopularity(popularity)
        .then((p) => setProducts(p))
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    } else if (relatedProductID) {
      setLoading(true);
      getRelatedProducts(relatedProductID)
        .then(setProducts)
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    }
  }, [popularity, relatedProductID]);

  const itemsVisible = Math.floor(
    containerWidth / (productCarouselCardWidth + 15),
  );
  const maxStartIndex = Math.max(products.length - itemsVisible, 0);

  const handleNext = useCallback(() => {
    setActiveIndex((prev) => Math.min(prev + 1, maxStartIndex));
  }, [maxStartIndex]);

  const handlePrev = useCallback(() => {
    setActiveIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  const TitleCmp = (
    <Typography
      sx={{
        fontSize: { sx: '1.75rem', sm: '36px' },
        fontWeight: 600,
        ...titleStyle,
      }}
    >
      {title}
    </Typography>
  );

  const Divider = (
    <Box
      sx={{
        backgroundColor: primary,
        height: '3px',
        width: '100px',
        ...dividerStyle,
      }}
    />
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: 3,
        alignItems: 'center',
      }}
    >
      {!!title && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            alignItems: 'center',
          }}
        >
          {titleStylePath ? (
            <EditableStyleWrapper path={titleStylePath}>
              {TitleCmp}
            </EditableStyleWrapper>
          ) : (
            TitleCmp
          )}

          {dividerStylePath ? (
            <EditableStyleWrapper path={dividerStylePath}>
              {Divider}
            </EditableStyleWrapper>
          ) : (
            Divider
          )}
        </Box>
      )}

      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          overflow: isMobile ? 'auto' : 'hidden',
          position: 'relative',
        }}
      >
        {!isMobile && (
          <>
            <IconButton
              onClick={handlePrev}
              disabled={activeIndex === 0}
              sx={{
                position: 'absolute',
                top: '50%',
                left: 0,
                transform: 'translateY(-50%)',
                zIndex: 2,
                backgroundColor: primary,
                color: secondary,
                '&:hover': {
                  backgroundColor: primary,
                  opacity: 0.8,
                },
              }}
            >
              <ChevronLeftIcon />
            </IconButton>

            <IconButton
              onClick={handleNext}
              disabled={activeIndex >= maxStartIndex}
              sx={{
                position: 'absolute',
                top: '50%',
                right: 0,
                transform: 'translateY(-50%)',
                zIndex: 2,
                backgroundColor: primary,
                color: secondary,
                '&:hover': {
                  backgroundColor: primary,
                  opacity: 0.8,
                },
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </>
        )}

        <Box
          sx={{
            width: `${(productCarouselCardWidth + 15) * products.length}px`,
            display: 'flex',
            flexDirection: 'row',
            transform: `translateX(-${
              (productCarouselCardWidth + 15) * activeIndex
            }px)`,
            transition: 'transform 0.5s',
            gap: '15px',
          }}
        >
          {loading
            ? Array(10)
                .fill(null)
                .map((_, i) => (
                  <Box key={i} width={productCarouselCardWidth}>
                    <ProductCardPlaceholder />
                  </Box>
                ))
            : products.map((product, i) => (
                <Box key={i} width={productCarouselCardWidth}>
                  <ProductCard productSummary={product} />
                </Box>
              ))}
        </Box>
      </Box>
    </Box>
  );
};
